import type { NostrEvent } from '@nostrify/nostrify';

/** Kind number for NIP-51 picture curation sets (albums). */
export const ALBUM_KIND = 30006;

/** Parsed album metadata from a kind 30006 event. */
export interface AlbumMeta {
  /** The raw Nostr event. */
  event: NostrEvent;
  /** Unique identifier (d tag). */
  dTag: string;
  /** Album title. */
  title: string;
  /** Cover image URL. */
  image?: string;
  /** Optional description. */
  description?: string;
  /** Event IDs of kind:20 pictures in the album. */
  photoIds: string[];
  /** Optional theme a-coordinate (e.g. "36767:<pubkey>:<d-tag>"). */
  themeCoordinate?: string;
  /** Author pubkey. */
  pubkey: string;
  /** Created at timestamp. */
  createdAt: number;
}

/**
 * Validate that a kind 30006 event has the required tags for an album.
 * Must have a `d` tag and a `title` tag.
 */
export function isValidAlbum(event: NostrEvent): boolean {
  if (event.kind !== ALBUM_KIND) return false;
  const dTag = event.tags.find(([n]) => n === 'd')?.[1];
  const title = event.tags.find(([n]) => n === 'title')?.[1];
  return !!dTag && !!title;
}

/**
 * Parse a kind 30006 event into AlbumMeta.
 * Returns undefined if the event is not a valid album.
 */
export function parseAlbum(event: NostrEvent): AlbumMeta | undefined {
  if (!isValidAlbum(event)) return undefined;

  const dTag = event.tags.find(([n]) => n === 'd')![1];
  const title = event.tags.find(([n]) => n === 'title')![1];
  const image = event.tags.find(([n]) => n === 'image')?.[1];
  const description = event.tags.find(([n]) => n === 'description')?.[1];
  const themeCoordinate = event.tags.find(([n]) => n === 'theme')?.[1];

  const photoIds = event.tags
    .filter(([n]) => n === 'e')
    .map(([, id]) => id)
    .filter((id): id is string => !!id);

  return {
    event,
    dTag,
    title,
    image,
    description,
    photoIds,
    themeCoordinate,
    pubkey: event.pubkey,
    createdAt: event.created_at,
  };
}

/**
 * Parse a theme a-coordinate string into its components.
 * Format: "<kind>:<pubkey>:<d-tag>"
 */
export function parseThemeCoordinate(coordinate: string): { kind: number; pubkey: string; dTag: string } | undefined {
  const parts = coordinate.split(':');
  if (parts.length < 3) return undefined;
  const kind = parseInt(parts[0], 10);
  if (isNaN(kind)) return undefined;
  return { kind, pubkey: parts[1], dTag: parts.slice(2).join(':') };
}

/**
 * Build the tags array for a kind 30006 album event.
 */
export function buildAlbumTags(opts: {
  dTag: string;
  title: string;
  image?: string;
  description?: string;
  photoIds: string[];
  themeCoordinate?: string;
}): string[][] {
  const tags: string[][] = [
    ['d', opts.dTag],
    ['title', opts.title],
    ['alt', `Photo album: ${opts.title}`],
  ];

  if (opts.image) {
    tags.push(['image', opts.image]);
  }
  if (opts.description) {
    tags.push(['description', opts.description]);
  }
  if (opts.themeCoordinate) {
    tags.push(['theme', opts.themeCoordinate]);
  }

  for (const id of opts.photoIds) {
    tags.push(['e', id]);
  }

  return tags;
}

/**
 * Generate a URL-safe slug from a title string.
 * Appends a short random suffix to avoid collisions.
 */
export function generateAlbumSlug(title: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);

  const suffix = Math.random().toString(36).slice(2, 6);
  return base ? `${base}-${suffix}` : suffix;
}
